import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link as LinkIcon, X, Camera, SquareCheckBig, CircleAlert, Trash2, User } from "lucide-react";
import Button from "../../../components/ui/Button";
import Card from "../../../components/ui/card";
import { useSelector } from "react-redux";
import { UserProfileFormData } from "../../user/models";
import { UpdateUserProfile, uploadUserImage, useAppDispatch } from "../../../redux";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import Spinner from "../../shared/components/Spinner";
import { GlobalService } from "../../shared/services";

export default function UserProfileSetting() {
    const { t } = useTranslation();
    const isLoading = useSelector(selectLoaderValue);

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const { user, token } = useSelector((state: any) => state.auth);
    const dispatch = useAppDispatch();
    const [formData, setFormData] = useState<UserProfileFormData>({
        firstName: user?.firstName,
        lastName: user?.lastName,
        title: user?.title || "",
        email: user?.email,
        phone: user?.phone || "",
        taxCode: user?.taxCode || "",
        bio: user?.bio || "",
        photo: user?.photo?.path || "",
        cover: user?.cover?.path || "",
        socialLinks: user?.socialLinks || [""],
    });

    const validate = (formData: any) => {
        const newErrors: { [key: string]: string } = {};
        const { name, title, email, bio, phone, taxCode, socialLinks, photo, firstName, lastName, cover } = formData;
        // if (!cover.trim()) newErrors.cover = "Cover is required";
        // if (!photo.trim()) newErrors.photo = "Photo is required";
        if (!firstName.trim()) newErrors.firstName = t("userProfileSettings.validation.firstNameRequired");
        if (!lastName.trim()) newErrors.lastName = t("userProfileSettings.validation.lastNameRequired");
        if (!title.trim()) newErrors.title = t("userProfileSettings.validation.titleRequired");
        if (!email.trim()) newErrors.email = t("userProfileSettings.validation.emailRequired");
        else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = t("userProfileSettings.validation.emailInvalid");

        if (!bio.trim()) newErrors.bio = t("userProfileSettings.validation.bioRequired");

        // ✅ Phone validation (required + format)
        if (!phone.trim()) {
            newErrors.phone = t("userProfileSettings.validation.phoneRequired");
        }

        // ✅ Tax Code validation (required)
        if (!taxCode.trim()) {
            newErrors.taxCode = t("userProfileSettings.validation.taxCodeRequired");
        }

        // const validSocialLinks = socialLinks.filter(link => link.trim() !== "");
        // if (validSocialLinks.length === 0) {
        //     newErrors.socialLinks = "At least one social link is required";
        // }

        return newErrors;
    };
    const handlePhotoUpload = async e => {
        const file = e.target.files?.[0];
        const { payload } = await dispatch(uploadUserImage({ file }));
        if (file) {
            const updateImageDataInBackground = {
                ...user,
                photo: {
                    id: payload?.file?.id,
                    path: payload?.file?.path,
                },
            };

            await dispatch(UpdateUserProfile({ id: user.id, data: updateImageDataInBackground }));
            const updatedFormData = {
                ...formData,
                photo: payload?.file?.path,
            };
            setFormData(updatedFormData);
            // setErrors(prevErrors => {
            //     const newErrors = { ...prevErrors };
            //     delete newErrors.photo;
            //     return newErrors;
            // });
        }
    };

    const handleInputChange = (field: string, value: string | number) => {
        const updatedFormData = {
            ...formData,
            [field]: value,
        };

        setFormData(updatedFormData);
        // setErrors(validate(updatedFormData));
    };

    const handleSave = async () => {
        const validationErrors = validate(formData);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            console.log("❌ Validation failed:", validationErrors);
            return;
        } else {
            const payload = {
                email: formData.email,
                firstName: formData?.firstName,
                lastName: formData?.lastName,
                bio: formData.bio,
                title: formData?.title,
                phone: formData?.phone,
                taxCode: formData?.taxCode,
                socialLinks: formData.socialLinks,
                role: {
                    id: user.role?.id.toString(),
                },
                status: {
                    id: user.status?.id,
                },
                // If phone number changed, set isPhoneVerified to false
                isPhoneVerified: formData?.phone !== user?.phone ? false : user?.isPhoneVerified,
            };
            dispatch(UpdateUserProfile({ id: user.id, data: payload }));
        }
    };
    const handleCoverPhotoUpload = async e => {
        const file = e.target.files?.[0];
        const { payload } = await dispatch(uploadUserImage({ file }));
        if (file) {
            const updateImageDataInBackground = {
                ...user,
                cover: {
                    id: payload?.file?.id,
                    path: payload?.file?.path,
                },
            };

            await dispatch(UpdateUserProfile({ id: user.id, data: updateImageDataInBackground }));
            const updatedFormData = {
                ...formData,
                cover: payload?.file?.path,
            };
            setFormData(updatedFormData);
            // setErrors(prevErrors => {
            //     const newErrors = { ...prevErrors };
            //     delete newErrors.cover;
            //     return newErrors;
            // });
        }
    };

    const handlePhotoRemove = async () => {
        try {
            // Clear the photo from local state
            const updatedFormData = {
                ...formData,
                photo: "",
            };
            setFormData(updatedFormData);

            // Update the user profile in the backend with null photo
            const updateImageDataInBackground = {
                ...user,
                photo: null, // Set photo to null to remove it
            };

            await dispatch(UpdateUserProfile({ id: user.id, data: updateImageDataInBackground }));
            
            GlobalService.showToast("success", t("common.success"), t("userProfileSettings.success.photoRemoved"));
        } catch (error) {
            console.error("Error removing photo:", error);
            GlobalService.showToast("error", t("common.error"), t("userProfileSettings.error.photoRemoveFailed"));
        }
    };
    return (
        <div>
            {isLoading && (
                <div className="absolute top-[50%] left-[50%]">
                    <Spinner color="gray" />
                </div>
            )}
            <div className={`${isLoading ? "opacity-25" : "opacity-100"}`}>
                <Card color={undefined} className="p-6">
                    <h2 className="text-xl font-semibold mb-6">{t("userProfileSettings.title")}</h2>

                    {/* Profile Photo */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium mb-2">{t("userProfileSettings.profilePhoto")}</label>
                        <div className="flex items-center gap-6">
                            <div className="relative">
                                <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
                                    {formData.photo && formData.photo.trim() !== "" ? (
                                        <img 
                                            src={formData.photo} 
                                            alt="Profile" 
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                // If the profile photo fails to load, hide the img and show User icon
                                                e.currentTarget.style.display = "none";
                                                const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                                                if (nextElement) {
                                                    nextElement.style.display = "flex";
                                                }
                                            }}
                                        />
                                    ) : null}
                                    <div className={`w-full h-full flex items-center justify-center ${formData.photo && formData.photo.trim() !== "" ? "hidden" : "flex"}`}>
                                        <User className="w-12 h-12 text-gray-400" />
                                    </div>
                                </div>
                                <div className="absolute bottom-0 right-0 flex gap-1">
                                    <label className="p-1.5 bg-blue-600 rounded-full text-white cursor-pointer hover:bg-blue-700">
                                        <Camera className="w-4 h-4" />
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handlePhotoUpload}
                                        />
                                    </label>
                                    {formData.photo && (
                                        <button
                                            onClick={handlePhotoRemove}
                                            className="p-1.5 bg-red-600 rounded-full text-white hover:bg-red-700 cursor-pointer"
                                            title={t("userProfileSettings.removePhoto")}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                                {/* {errors.photo && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.photo}
                                    </div>
                                )} */}
                            </div>
                            <div className="text-sm text-gray-600">
                                <p>{t("userProfileSettings.recommendedSize")}</p>
                                <p>{t("userProfileSettings.maxSize")}</p>
                                {/* {errors.photo && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.photo}
                                    </div>
                                )} */}
                            </div>
                        </div>
                    </div>

                    {/* Basic Information */}
                    <div className="space-y-4">
                        <div className="w-full my-2">
                            <label className="block text-sm font-medium mb-1">{t("userProfileSettings.coverPhoto")}</label>
                            <div
                                className="w-full bg-gray-200 h-40 relative rounded-md overflow-hidden"
                                style={{
                                    backgroundImage: `url(${formData.cover})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}>
                                <label
                                    htmlFor="cover-image-upload"
                                    className="absolute inset-0 z-10 cursor-pointer"
                                    style={{ pointerEvents: "auto" }}
                                    onClick={e => e.stopPropagation()}>
                                    <input
                                        id="cover-image-upload"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleCoverPhotoUpload}
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center transition-opacity">
                                        <div className="p-2 rounded-full bg-blue-600 absolute right-2 bottom-2">
                                            <Camera className="w-6 h-6 text-white" />
                                        </div>
                                    </div>
                                </label>
                            </div>
                            {/* {errors.cover && (
                                <div className="flex items-center text-red-600 text-sm mt-1">
                                    <CircleAlert className="w-4 h-4 mr-1" />
                                    {errors.cover}
                                </div>
                            )} */}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("userProfileSettings.firstName")}*</label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={e => handleInputChange("firstName", e.target.value)}
                                    className="w-full p-2 border rounded-lg"
                                />
                                {errors.firstName && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.firstName}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">{t("userProfileSettings.lastName")}*</label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={e => handleInputChange("lastName", e.target.value)}
                                    className="w-full p-2 border rounded-lg"
                                />
                                {errors.lastName && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.lastName}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("userProfileSettings.email")}*</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    disabled
                                    onChange={e => handleInputChange("email", e.target.value)}
                                    className="w-full p-2 border rounded-lg"
                                />
                                {errors.email && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.email}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("userProfileSettings.phone")}*</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-black-500 text-sm">+</span>
                                    </div>
                                    <input
                                        type="tel"
                                        value={formData.phone.replace(/^\+/, "")}
                                        onChange={(e) => {
                                            let value = e.target.value.replace(/\D/g, "");
                                            if (value) {
                                                value = "+" + value;
                                            }
                                            handleInputChange("phone", value);
                                        }}
                                        placeholder="1 (555) 123-4567"
                                        className="w-full pl-8 pr-3 py-2 border rounded-lg"
                                    />
                                </div>
                                {errors.phone && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.phone}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{t("userProfileSettings.taxCode")}*</label>
                                <input
                                    type="text"
                                    value={formData.taxCode}
                                    onChange={e => handleInputChange("taxCode", e.target.value)}
                                    className="w-full p-2 border rounded-lg"
                                />
                                {errors.taxCode && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.taxCode}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">{t("userProfileSettings.title")}*</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={e => handleInputChange("title", e.target.value)}
                                className="w-full p-2 border rounded-lg"
                            />
                            {errors.title && (
                                <div className="flex items-center text-red-600 text-sm mt-1">
                                    <CircleAlert className="w-4 h-4 mr-1" />
                                    {errors.title}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">{t("userProfileSettings.bio")}*</label>
                            <textarea
                                value={formData.bio}
                                onChange={e => handleInputChange("bio", e.target.value)}
                                rows={4}
                                className="w-full p-2 border rounded-lg"
                            />
                            {errors.bio && (
                                <div className="flex items-center text-red-600 text-sm mt-1">
                                    <CircleAlert className="w-4 h-4 mr-1" />
                                    {errors.bio}
                                </div>
                            )}
                        </div>
                        {/* Social Links */}
                        <div>
                            <label className="block text-sm font-medium mb-2">{t("userProfileSettings.socialLinks")}</label>
                            <div className="space-y-2">
                                {formData.socialLinks.map((url, index) => (
                                    <div key={index} className="flex gap-2">
                                        <input
                                            type="url"
                                            value={url}
                                            onChange={e => {
                                                const updatedLinks = [...formData.socialLinks];
                                                updatedLinks[index] = e.target.value;
                                                const updatedFormData = {
                                                    ...formData,
                                                    socialLinks: updatedLinks,
                                                };

                                                setFormData(updatedFormData);
                                                // setErrors(validate(updatedFormData));
                                            }}
                                            placeholder={`Social Link #${index + 1}`}
                                            className="flex-1 p-2 border rounded-lg"
                                        />
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            onClick={() => {
                                                const updatedLinks = formData.socialLinks.filter((_, i) => i !== index);
                                                const updatedFormData = {
                                                    ...formData,
                                                    socialLinks: updatedLinks,
                                                };

                                                setFormData(updatedFormData);
                                                // setErrors(validate(updatedFormData));
                                            }}>
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                                <Button
                                    variant="outline"
                                    className="w-full"
                                    onClick={() => {
                                        const updatedFormData = {
                                            ...formData,
                                            socialLinks: [...formData.socialLinks, ""],
                                        };

                                        setFormData(updatedFormData);
                                        // setErrors(validate(updatedFormData));
                                    }}>
                                    {t("userProfileSettings.addLink")}
                                </Button>

                                {/* {errors.socialLinks && (
                                    <div className="flex items-center text-red-600 text-sm mt-1">
                                        <CircleAlert className="w-4 h-4 mr-1" />
                                        {errors.socialLinks}
                                    </div>
                                )} */}
                                {/* <Button
                                variant="outline"
                                className="w-full"
                                onClick={() => {
                                    setFormData(prev => ({
                                        ...prev,
                                        socialLinks: {
                                            ...prev.socialLinks,
                                            [`platform${Date.now()}`]: "",
                                        },
                                    }));
                                }}>
                                Add Social Link
                            </Button> */}
                            </div>
                        </div>
                    </div>
                </Card>

                <div className="flex justify-end mt-4">
                    <button
                        className={"px-4 py-2 text-white rounded-md flex items-center bg-blue-600 hover:bg-blue-700"}
                        onClick={handleSave}>
                        <SquareCheckBig className="h-5" />
                        <span className="pl-2">{t("userProfileSettings.saveChanges")}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
